/**
 * Match screen (contracts-plan.md 7.5, R20, G8, G9, G11; ledger item 241, WP-97b).
 * The lines of one version that a proposal run could not match, each with ranked
 * master-list candidates recomputed by the server from the LIVE lists. A person picks
 * one, creates a new entry, or says the line is not a rate-card line; the pick is kept
 * as an alias so the next budget matches at once, and the line is proposed into the
 * version's open batch.
 *
 * Candidates are OFFERED, never applied (G11): no score is shown as a percentage or a
 * confidence, only the reason text the server gives. A resolved row is replaced in place
 * by one line saying what happened and is not removed, so a person sees what they did
 * (G9). A failure shows the server's own message under that row and leaves it usable;
 * the other rows keep working (no cross-row transaction, 16.1).
 */
import { useEffect, useState } from 'react';
import Modal from '../../../ui/Modal';
import EmptyState from '../../../ui/EmptyState';
import { fieldCls, outlineBtnCls, primaryBtnCls } from '../../../ui/styles';
import { Search } from 'lucide-react';
import { decideUnmatchedLine, getUnmatchedLines } from '../../api/rateCard';
import type { ProposalOutcome, RcUnmatchedDecideInput, RcUnmatchedLine } from '../../api/rateCard';

interface Props {
  versionId: string;
  onClose: () => void;
  /** Called once, on close, when at least one line was resolved. */
  onChanged: () => void;
}

const PAGE = 200;
const CANDIDATE_LIMIT = 5;

interface RowState {
  /** 'c:<entryId>' for a candidate, 'new', or 'reject'. */
  choice: string | null;
  newName: string;
  busy: boolean;
  error: string | null;
  result: string | null;
}

const EMPTY_ROW: RowState = { choice: null, newName: '', busy: false, error: null, result: null };

const MATCHED_ON: Record<string, string> = {
  official_name: 'official name',
  alias: 'alias',
  code: 'code',
};

function errorText(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: unknown } } } | null | undefined)?.response?.data;
  if (data && typeof data.error === 'string' && data.error) return data.error;
  const message = (err as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' && message ? message : fallback;
}

function datapointsText(n: number): string {
  return `${n} datapoint${n === 1 ? '' : 's'}`;
}

/** Why a proposal produced no datapoint, in plain words. */
function whyNotProposed(proposal: ProposalOutcome | undefined): string {
  if (!proposal) return 'the server reported no proposal';
  if (proposal.reason === 'version_not_executed') return 'this version is not executed';
  if (proposal.reason === 'not_requested') return 'not requested';
  if (proposal.reason === 'proposal_failed') return `the proposal failed${proposal.error ? `: ${proposal.error}` : ''}`;
  const skipped = (proposal.skippedByReason ?? []).map((s) => `${s.reason.replace(/_/g, ' ')} (${s.count})`);
  if (skipped.length > 0) return `skipped: ${skipped.join(', ')}`;
  return proposal.reason ? proposal.reason.replace(/_/g, ' ') : 'no line was proposed';
}

function resultText(outcome: RcUnmatchedDecideInput['outcome'], name: string | null, proposal: ProposalOutcome | undefined): string {
  if (outcome === 'rejected') return 'Recorded; nothing proposed - marked as not a rate-card line.';
  const who = outcome === 'new_entry' ? `Created ${name ?? 'a new entry'} and matched to it` : name ? `Matched to ${name}` : 'Matched';
  if (proposal?.ran && proposal.proposed > 0) return `${who}; ${datapointsText(proposal.proposed)} proposed.`;
  if (proposal?.reason === 'version_not_executed') return 'Recorded; nothing proposed - this version is not executed.';
  return `${who}; nothing proposed - ${whyNotProposed(proposal)}.`;
}

function statedNumber(line: RcUnmatchedLine): string | null {
  const n = line.unit_cost_stated ?? line.line_total_stated;
  return typeof n === 'number' ? n.toLocaleString('en-US', { maximumFractionDigits: 4 }) : null;
}

export function MatchScreen({ versionId, onClose, onChanged }: Props) {
  const [lines, setLines] = useState<RcUnmatchedLine[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [resolved, setResolved] = useState(0);
  // Lines resolved to a match leave the server's unmatched list; a "not a rate-card line"
  // decision does not. The next page's offset must skip only the ones that left.
  const [leftServerList, setLeftServerList] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLines(null);
    setLoadError(null);
    getUnmatchedLines({ versionId, limit: PAGE, candidateLimit: CANDIDATE_LIMIT })
      .then((result) => {
        if (cancelled) return;
        setLines(result?.lines ?? []);
        setHasMore(result?.page?.hasMore === true);
      })
      .catch((err) => {
        if (cancelled) return;
        setLines([]);
        setLoadError(errorText(err, 'Could not load the unmatched lines.'));
      });
    return () => {
      cancelled = true;
    };
  }, [versionId]);

  function rowOf(id: string): RowState {
    return rows[id] ?? EMPTY_ROW;
  }

  function patchRow(id: string, patch: Partial<RowState>) {
    setRows((prev) => ({ ...prev, [id]: { ...(prev[id] ?? EMPTY_ROW), ...patch } }));
  }

  function choose(line: RcUnmatchedLine, choice: string) {
    patchRow(line.id, {
      choice,
      error: null,
      ...(choice === 'new' && rowOf(line.id).newName === '' ? { newName: line.label_raw } : {}),
    });
  }

  async function loadMore() {
    if (lines === null || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await getUnmatchedLines({
        versionId,
        limit: PAGE,
        offset: Math.max(0, lines.length - leftServerList),
        candidateLimit: CANDIDATE_LIMIT,
      });
      const incoming = result?.lines ?? [];
      setLines((prev) => {
        const base = prev ?? [];
        const known = new Set(base.map((l) => l.id));
        return [...base, ...incoming.filter((l) => !known.has(l.id))];
      });
      setHasMore(result?.page?.hasMore === true);
    } catch (err) {
      setLoadError(errorText(err, 'Could not load more unmatched lines.'));
    } finally {
      setLoadingMore(false);
    }
  }

  async function submit(line: RcUnmatchedLine, confirmNow: boolean) {
    const state = rowOf(line.id);
    let input: RcUnmatchedDecideInput;
    let name: string | null = null;
    if (confirmNow && line.now_matches) {
      input = { lineId: line.id, outcome: 'matched', entryId: line.now_matches.entryId };
    } else if (state.choice === 'reject') {
      input = { lineId: line.id, outcome: 'rejected' };
    } else if (state.choice === 'new') {
      const typed = state.newName.trim();
      if (typed === '') {
        patchRow(line.id, { error: 'Type a name for the new entry.' });
        return;
      }
      input = { lineId: line.id, outcome: 'new_entry', newEntry: { official_name: typed } };
      name = typed;
    } else if (state.choice && state.choice.startsWith('c:')) {
      const entryId = state.choice.slice(2);
      input = { lineId: line.id, outcome: 'matched', entryId };
      name = line.candidates.find((c) => c.entry.id === entryId)?.entry.official_name ?? null;
    } else {
      return;
    }
    patchRow(line.id, { busy: true, error: null });
    try {
      const response = await decideUnmatchedLine(input);
      const entryName = response?.entry?.official_name;
      const created = typeof entryName === 'string' ? entryName : null;
      patchRow(line.id, { busy: false, result: resultText(input.outcome, created ?? name, response?.proposal) });
      setResolved((n) => n + 1);
      if (input.outcome !== 'rejected') setLeftServerList((n) => n + 1);
    } catch (err) {
      patchRow(line.id, { busy: false, error: errorText(err, 'Could not record that decision.') });
    }
  }

  function handleClose() {
    if (resolved > 0) onChanged();
    onClose();
  }

  return (
    <Modal isOpen onClose={handleClose} title="Match unmatched lines" maxWidth="max-w-4xl">
      <div data-testid="rc-match-screen" className="space-y-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          These lines of version {versionId.slice(0, 8)} did not match a master-list entry. Your pick is remembered as an alias, so the
          next budget matches without asking.
        </p>

        {lines === null && !loadError && <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>}
        {loadError && <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>}
        {lines !== null && lines.length === 0 && !loadError && (
          <EmptyState icon={Search} title="No unmatched lines" hint="Every matchable line of this version is matched." variant="card" />
        )}

        {lines !== null && lines.length > 0 && (
          <ul className="divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
            {lines.map((line) => {
              const state = rowOf(line.id);
              const stated = statedNumber(line);
              const groupName = `match-${line.id}`;
              const canUse =
                !state.busy &&
                (state.choice === 'reject' ||
                  (state.choice === 'new' && state.newName.trim() !== '') ||
                  (state.choice !== null && state.choice.startsWith('c:')));
              return (
                <li key={line.id} className="px-4 py-3" data-testid={`rc-match-line-${line.id}`}>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {line.sheet_name ?? 'Sheet'}
                    {line.row !== null && line.row !== undefined ? ` - row ${line.row}` : ''}
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{line.label_raw}</span>
                    {stated !== null && <span className="text-xs tabular-nums text-gray-600 dark:text-gray-400">Stated {stated}</span>}
                  </div>

                  {state.result !== null ? (
                    <p className="mt-2 text-sm text-gray-700 dark:text-gray-200" data-testid={`rc-match-result-${line.id}`}>
                      {state.result}
                    </p>
                  ) : line.now_matches ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        This label now matches a master-list entry (on {MATCHED_ON[line.now_matches.matchLabel] ?? line.now_matches.matchLabel}).
                      </span>
                      <button
                        type="button"
                        className={primaryBtnCls}
                        disabled={state.busy}
                        onClick={() => submit(line, true)}
                        data-testid={`rc-match-use-${line.id}`}
                      >
                        Confirm match
                      </button>
                    </div>
                  ) : (
                    <fieldset className="mt-2" disabled={state.busy}>
                      <legend className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                        Offered - nothing is applied until you pick
                      </legend>
                      <div className="space-y-1">
                        {line.candidates.map((c) => (
                          <label
                            key={c.entry.id}
                            className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-sm text-gray-900 hover:bg-gray-50 sm:min-h-0 dark:text-gray-100 dark:hover:bg-gray-700/50"
                            title={c.reasons?.[0]?.detail ?? undefined}
                          >
                            <input
                              type="radio"
                              name={groupName}
                              className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                              checked={state.choice === `c:${c.entry.id}`}
                              onChange={() => choose(line, `c:${c.entry.id}`)}
                              data-testid={`rc-match-candidate-${line.id}-${c.entry.id}`}
                            />
                            <span className="font-medium">{c.entry.official_name}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              matched on {MATCHED_ON[c.matchLabel] ?? c.matchLabel}
                            </span>
                          </label>
                        ))}
                        <label className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-sm text-gray-900 hover:bg-gray-50 sm:min-h-0 dark:text-gray-100 dark:hover:bg-gray-700/50">
                          <input
                            type="radio"
                            name={groupName}
                            className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                            checked={state.choice === 'new'}
                            onChange={() => choose(line, 'new')}
                            data-testid={`rc-match-new-${line.id}`}
                          />
                          <span>Create a new entry</span>
                        </label>
                        {state.choice === 'new' && (
                          <input
                            type="text"
                            className={`${fieldCls} ml-6 w-full max-w-md`}
                            value={state.newName}
                            onChange={(e) => patchRow(line.id, { newName: e.target.value })}
                            aria-label="Name of the new entry"
                            data-testid={`rc-match-name-${line.id}`}
                          />
                        )}
                        <label className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-sm text-gray-900 hover:bg-gray-50 sm:min-h-0 dark:text-gray-100 dark:hover:bg-gray-700/50">
                          <input
                            type="radio"
                            name={groupName}
                            className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                            checked={state.choice === 'reject'}
                            onChange={() => choose(line, 'reject')}
                            data-testid={`rc-match-reject-${line.id}`}
                          />
                          <span>Not a rate-card line</span>
                        </label>
                      </div>
                      <div className="mt-2">
                        <button
                          type="button"
                          className={primaryBtnCls}
                          disabled={!canUse}
                          onClick={() => submit(line, false)}
                          data-testid={`rc-match-use-${line.id}`}
                        >
                          Use this
                        </button>
                      </div>
                    </fieldset>
                  )}

                  {state.error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{state.error}</p>}
                </li>
              );
            })}
          </ul>
        )}

        {hasMore && (
          <div>
            <button type="button" className={outlineBtnCls()} disabled={loadingMore} onClick={loadMore} data-testid="rc-match-more">
              Load more
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default MatchScreen;
